import React, { useEffect, useState } from 'react';
import { userService } from '../services/api';
import Sidebar from '../components/Sidebar';
import './Dashboard.css';

const PAGE_SIZE = 10;

const LIGHT_RANK  = { GREEN: 0, ORANGE: 1, RED: 2 };
const LIGHT_LABEL = { GREEN: 'im Rahmen', ORANGE: 'außerhalb', RED: 'dauerhaft außerhalb' };

function getInitials(alias, username) {
  const name = alias || username || '?';
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

function formatHours(h) {
  const sign = h >= 0 ? '+' : '−';
  return `${sign}${Math.abs(h).toFixed(1)}`;
}

function formatSignedHours(h) {
  const sign = h >= 0 ? '+' : '−';
  const val  = Math.abs(h).toFixed(1).replace(/\.0$/, '').replace('.', ',');
  return `${sign}${val} h`;
}

function formatPercent(p) {
  return p.toFixed(1).replace(/\.0$/, '').replace('.', ',');
}

function formatDays(n) {
  return (n ?? 0).toFixed(1).replace(/\.0$/, '').replace('.', ',');
}

function formatMonth(dateStr) {
  return new Date(dateStr).toLocaleDateString('de-DE', { year: 'numeric', month: 'long' });
}

function Ampel({ light, lowerLimitHours, upperLimitHours }) {
  const state = light || 'GREEN';
  return (
    <div className="ampel-cell" title={LIGHT_LABEL[state]}>
      <span className={`ampel-dot ampel-dot--${state.toLowerCase()}`} />
      <div className="ampel-meta">
        <span className="ampel-label">{LIGHT_LABEL[state]}</span>
        <span className="ampel-range">
          {formatSignedHours(lowerLimitHours)} … {formatSignedHours(upperLimitHours)}
        </span>
      </div>
    </div>
  );
}

function HistoryModal({ user, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    let active = true;
    userService.getUserHistory(user.id)
      .then(res => { if (active) setHistory(res.data); })
      .catch(() => { if (active) setError('Historie konnte nicht geladen werden.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [user.id]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">{user.alias || user.username}</h2>
            <p className="modal-sub">Zeitkonto-Historie · monatliche Snapshots</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Schließen">✕</button>
        </div>

        {loading && (
          <div className="modal-state"><div className="dash-spinner" /><span>Lade Historie…</span></div>
        )}
        {error && <div className="modal-state"><span className="dash-error-text">{error}</span></div>}

        {!loading && !error && history.length === 0 && (
          <div className="modal-state"><span>Noch keine Snapshots vorhanden.</span></div>
        )}

        {!loading && !error && history.length > 0 && (
          <ul className="history-list">
            {history.map((s, i) => (
              <li key={i} className="history-row">
                <span className={`ampel-dot ampel-dot--${s.outsideLimits ? 'red' : 'green'}`} />
                <span className="history-month">{formatMonth(s.snapshotDate)}</span>
                <span className="history-balance" data-sign={s.balanceHours >= 0 ? 'pos' : 'neg'}>
                  {formatHours(s.balanceHours)}&thinsp;h
                </span>
                <span className="history-range">
                  {formatSignedHours(s.lowerLimit)} … {formatSignedHours(s.upperLimit)}
                </span>
                <span className="history-meta">
                  Krank (Vormonat): {formatDays(s.sickDaysMonth)} · Resturlaub: {formatDays(s.openVacationDays)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const FILTERS = [
  { key: 'all',  label: 'Alle' },
  { key: 'pos',  label: 'Überstunden' },
  { key: 'neg',  label: 'Minusstunden' },
];

function SortIcon({ active, dir }) {
  return (
    <span className={`sort-icon${active ? ' sort-icon--active' : ''}`}>
      {active && dir === 'asc' ? '↑' : active && dir === 'desc' ? '↓' : '↕'}
    </span>
  );
}

function Pagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  if (totalPages <= 7) {
    for (let i = 0; i < totalPages; i++) pages.push(i);
  } else {
    pages.push(0);
    if (page > 2) pages.push('…');
    for (let i = Math.max(1, page - 1); i <= Math.min(totalPages - 2, page + 1); i++) {
      pages.push(i);
    }
    if (page < totalPages - 3) pages.push('…');
    pages.push(totalPages - 1);
  }

  return (
    <div className="pagination">
      <button
        className="page-btn"
        onClick={() => onPageChange(page - 1)}
        disabled={page === 0}
      >
        ←
      </button>
      {pages.map((p, i) =>
        p === '…'
          ? <span key={`ellipsis-${i}`} className="page-ellipsis">…</span>
          : <button
              key={p}
              className={`page-btn${p === page ? ' page-btn--active' : ''}`}
              onClick={() => onPageChange(p)}
            >
              {p + 1}
            </button>
      )}
      <button
        className="page-btn"
        onClick={() => onPageChange(page + 1)}
        disabled={page === totalPages - 1}
      >
        →
      </button>
    </div>
  );
}

function Dashboard() {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [query,  setQuery]    = useState('');
  const [filter, setFilter]   = useState('all');
  const [sort,   setSort]     = useState({ key: 'status', dir: 'desc' });
  const [page,   setPage]     = useState(0);
  const [historyUser, setHistoryUser] = useState(null);

  useEffect(() => {
    userService.getAll()
      .then(res => setUsers(res.data))
      .catch(() => setError('Mitarbeiterdaten konnten nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { setPage(0); }, [query, filter, sort]);

  function toggleSort(key) {
    setSort(s => s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'name' ? 'asc' : 'desc' }
    );
  }

  const displayUsers = users
    .filter(u => {
      if (filter === 'pos') return u.timeAccountHours >= 0;
      if (filter === 'neg') return u.timeAccountHours <  0;
      return true;
    })
    .filter(u => {
      if (!query) return true;
      const q = query.toLowerCase();
      return (u.alias  || '').toLowerCase().includes(q)
          || (u.username || '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') {
        const na = (a.alias || a.username).toLowerCase();
        const nb = (b.alias || b.username).toLowerCase();
        cmp = na.localeCompare(nb, 'de');
      } else if (sort.key === 'hours') {
        cmp = a.timeAccountHours - b.timeAccountHours;
      } else if (sort.key === 'weekly') {
        cmp = a.weeklyPercent - b.weeklyPercent;
      } else if (sort.key === 'sickYear') {
        cmp = (a.sickDays12Months ?? 0) - (b.sickDays12Months ?? 0);
      } else if (sort.key === 'openVac') {
        cmp = (a.plannedVacationDays ?? 0) - (b.plannedVacationDays ?? 0);
      } else if (sort.key === 'status') {
        cmp = (LIGHT_RANK[a.trafficLight] ?? 0) - (LIGHT_RANK[b.trafficLight] ?? 0);
      }
      return sort.dir === 'asc' ? cmp : -cmp;
    });

  const totalPages = Math.ceil(displayUsers.length / PAGE_SIZE);
  const pagedUsers = displayUsers.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="dash-root">
      <Sidebar />

      <main className="dash-main">
        <header className="dash-header">
          <div>
            <h1 className="dash-title">Zeitkonten aller Mitarbeiter*innen</h1>
            <p className="dash-date">
              {new Date().toLocaleDateString('de-DE', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
              })}
            </p>
          </div>
        </header>

        {loading && (
          <div className="dash-state">
            <div className="dash-spinner" />
            <span>Lade Daten…</span>
          </div>
        )}
        {error && <div className="dash-state"><span className="dash-error-text">{error}</span></div>}

        {!loading && !error && (
          <div className="table-card">
            <div className="toolbar">
              <div className="search-wrap">
                <span className="search-icon">⌕</span>
                <input
                  className="search-input"
                  type="text"
                  placeholder="Mitarbeiter*innen suchen…"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                />
                {query && (
                  <button className="search-clear" onClick={() => setQuery('')}>✕</button>
                )}
              </div>
              <div className="filter-chips">
                {FILTERS.map(f => (
                  <button
                    key={f.key}
                    className={`filter-chip${filter === f.key ? ' filter-chip--active' : ''}`}
                    onClick={() => setFilter(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <span className="table-count">
                Seite {page + 1} / {totalPages || 1} · {displayUsers.length} von {users.length}
              </span>
            </div>

            <table className="user-table">
              <thead>
                <tr>
                  <th className="th-sort" onClick={() => toggleSort('name')}>
                    Mitarbeiter*innen <SortIcon active={sort.key === 'name'} dir={sort.dir} />
                  </th>
                  <th className="th-center th-sort" onClick={() => toggleSort('hours')}>
                    Stundenkonto <SortIcon active={sort.key === 'hours'} dir={sort.dir} />
                  </th>
                  <th className="th-center th-sort" onClick={() => toggleSort('weekly')}>
                    Sollarbeitszeit <SortIcon active={sort.key === 'weekly'} dir={sort.dir} />
                  </th>
                  <th className="th-center th-sort" onClick={() => toggleSort('sickYear')}
                      title="Krankheitstage der letzten 12 Monate">
                    Krankheitstage <SortIcon active={sort.key === 'sickYear'} dir={sort.dir} />
                  </th>
                  <th className="th-center th-sort" onClick={() => toggleSort('openVac')}
                      title="Im Jahr eingetragene Urlaubstage inkl. zukünftig gebuchter">
                    Geplanter Urlaub <SortIcon active={sort.key === 'openVac'} dir={sort.dir} />
                  </th>
                  <th className="th-bar th-sort" onClick={() => toggleSort('status')}>
                    Ampel <SortIcon active={sort.key === 'status'} dir={sort.dir} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {pagedUsers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="td-empty">Keine Mitarbeiter*innen gefunden.</td>
                  </tr>
                )}
                {pagedUsers.map((u, i) => (
                  <tr
                    key={u.id}
                    className="user-row user-row--clickable"
                    style={{ animationDelay: `${i * 40}ms` }}
                    onClick={() => setHistoryUser(u)}
                    title="Zeitkonto-Historie anzeigen"
                  >
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar">{getInitials(u.alias, u.username)}</div>
                        <div className="user-meta">
                          <span className="user-name">{u.alias || u.username}</span>
                          {u.alias && <span className="user-sub">{u.username}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="td-hours">
                      <div className="hours-badge" data-sign={u.timeAccountHours >= 0 ? 'pos' : 'neg'}>
                        <span className="hours-value">{formatHours(u.timeAccountHours)}&thinsp;h</span>
                      </div>
                    </td>
                    <td className="td-center">
                      <div className="weekly-cell">
                        <span className="weekly-hours">{formatPercent(u.weeklyHours)}&thinsp;h</span>
                        <span className="weekly-percent">{formatPercent(u.weeklyPercent)} %</span>
                      </div>
                    </td>
                    <td className="td-center">
                      <div className="days-cell">
                        <span className="days-value">{formatDays(u.sickDays12Months)}</span>
                        <span className="days-sub">im Monat: {formatDays(u.sickDaysMonth)}</span>
                      </div>
                    </td>
                    <td className="td-center">
                      <div className="days-cell">
                        <span className="days-value">{formatDays(u.plannedVacationDays)}</span>
                        <span className="days-sub">von {formatDays(u.vacationEntitlementDays)}</span>
                      </div>
                    </td>
                    <td className="td-bar">
                      <Ampel
                        light={u.trafficLight}
                        lowerLimitHours={u.lowerLimitHours}
                        upperLimitHours={u.upperLimitHours}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </main>

      {historyUser && (
        <HistoryModal user={historyUser} onClose={() => setHistoryUser(null)} />
      )}
    </div>
  );
}

export default Dashboard;
